import { useElementWidth } from '@/hooks/useElementWidth'
import { colors } from '@/theme'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Box, IconButton } from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'
import StageCard from './StageCard'
import type { Stage } from './types'

interface StatusTimelineProps {
  stages: Stage[]
}

const arrowButtonSx = {
  position: 'absolute',
  top: '18%',
  transform: 'translateY(-70%)',
  zIndex: 2,
  bgcolor: colors.background.paper,
  border: `1px solid ${colors.border.default}`,
  width: 32,
  height: 32,
  '&:disabled': { opacity: 0.4 }
} as const

export default function StatusTimeline({ stages }: StatusTimelineProps) {
  const { ref: viewportRef, itemWidth } = useElementWidth<HTMLDivElement>(6)
  const scrollerRef = useRef<HTMLDivElement | null>(null)

  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateScrollButtonAvailability = useCallback(() => {
    const el = scrollerRef.current
    if (!el) return
    const maxScrollLeft = el.scrollWidth - el.clientWidth
    setCanScrollLeft(el.scrollLeft > 1)
    setCanScrollRight(el.scrollLeft < maxScrollLeft - 1)
  }, [])

  const rafIdRef = useRef<number | null>(null)
  // biome-ignore lint/correctness/useExhaustiveDependencies: updateScrollButtonAvailability is stable
  const throttledUpdateScrollButtonAvailability = useCallback(() => {
    if (rafIdRef.current !== null) return
    rafIdRef.current = requestAnimationFrame(() => {
      updateScrollButtonAvailability()
      rafIdRef.current = null
    })
  }, [])

  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current)
      }
    }
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: updateScrollButtonAvailability is stable
  useEffect(() => {
    const el = scrollerRef.current
    if (!el || !itemWidth) return

    requestAnimationFrame(() => {
      el.scrollLeft = el.scrollWidth
      updateScrollButtonAvailability()
    })
  }, [stages.length, itemWidth])

  const scrollByItems = (dir: 'left' | 'right') => {
    const el = scrollerRef.current
    if (!el || !itemWidth) return
    el.scrollBy({ left: (dir === 'left' ? -1 : 1) * itemWidth, behavior: 'smooth' })
  }

  const showArrows = stages.length > 6

  if (!stages.length) return null

  return (
    <Box sx={{ position: 'relative', mb: 3 }}>
      <Box ref={viewportRef} sx={{ position: 'relative' }}>
        {showArrows && (
          <>
            <IconButton
              onClick={() => scrollByItems('left')}
              disabled={!canScrollLeft}
              sx={{ ...arrowButtonSx, left: -12 }}
              aria-label='Scroll left'
            >
              <ChevronLeftIcon fontSize='small' />
            </IconButton>
            <IconButton
              onClick={() => scrollByItems('right')}
              disabled={!canScrollRight}
              sx={{ ...arrowButtonSx, right: -12 }}
              aria-label='Scroll right'
            >
              <ChevronRightIcon fontSize='small' />
            </IconButton>
          </>
        )}

        <Box
          ref={scrollerRef}
          onScroll={throttledUpdateScrollButtonAvailability}
          sx={{
            overflowX: 'auto',
            overflowY: 'hidden',
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
            px: showArrows ? 4 : 0
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', width: 'max-content' }}>
            {stages.map((stage, index) => (
              <StageCard key={stage.id} stage={stage} itemWidth={itemWidth} isLast={index === stages.length - 1} />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
